import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  useNavigationType,
  Navigate,
  useOutletContext,
} from "react-router-dom";
import { useLanguage } from "../Translations/LanguageContext";
import LandingFooter from "../components/LandingPage/components/LandingFooter";
import "../components/LandingPage/LandingPage.css";
import CookieConsent from "../components/CookieConsent/CookieConsent";
import api, { fetchCSRFToken } from "../api";
import usePostHog from "./posthog";
import { initGA4, trackPageView } from "./ga4";
import { hasAnalyticsConsent, COOKIE_CONSENT_CHANGED_EVENT } from "../utils/cookieConsent";
import AppLoadingSkeleton from "./AppLoadingSkeleton";

/**
 * Protects routes that require authentication.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render if authenticated.
 * @param {string} props.redirectTo - The path to redirect to if not authenticated.
 * @returns {React.ReactElement} The protected route or a redirect.
 */
export const ProtectedRoute = ({ children, redirectTo, adminOnly = false }) => {
  const { user, isLoading } = useOutletContext();

  if (isLoading) {
    return <div></div>;
  }

  if (!user) {
    return <Navigate to={redirectTo} />;
  }

  if (adminOnly && !user.isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

/**
 * Main content component for the application.
 * Handles authentication, routing, and provides context to child components.
 *
 * @component
 * @returns {React.ReactElement} The main application content.
 */
function AppContent() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const posthog = usePostHog();
  const isProduction = process.env.REACT_APP_ENABLE_POSTHOG === "true" && hasAnalyticsConsent();

  // Reset scroll to the top on forward navigation (PUSH/REPLACE), leaving
  // back/forward (POP) for the browser to restore. react-router's
  // <ScrollRestoration> only touches window scroll, which isn't reliable
  // here — html AND body both carry `overflow-y: scroll` (app.css), so on
  // some browsers body is the actual scroller (hence the window +
  // document.body belt-and-suspenders already in Login.js). Without this,
  // clicking from a scrolled-down homepage into a place/guide kept the old
  // offset: the shorter loading screen clamped it near the bottom, showing
  // the footer for a beat before the article rendered. useLayoutEffect so
  // the jump lands before the browser paints the new route.
  useLayoutEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [location.pathname, navigationType]);

  const publicRoutes = [
    "robots.txt",
    "/",
    "/landing",
    "/events",
    "/forgot-password",
    "/reset-password",
    "/verify-ticket",
    "/privacy-policy",
    "/places",
  ];

  // Boundary-aware check reusing the same publicRoutes list above (not a second,
  // possibly-inconsistent list). Unlike the plain `startsWith` used by the
  // redirect effect below, "/" here only matches the literal root — otherwise
  // it would match every path and defeat the loading gate for protected routes.
  const isPublicPath = publicRoutes.some((route) =>
    route === "/"
      ? location.pathname === "/"
      : location.pathname === route || location.pathname.startsWith(route + "/")
  );

  const checkAuth = useCallback(async () => {
    try {
      const authResponse = await api.checkAuth();
      if (authResponse.status === 204) {
        console.log("No user is logged in");
        // No content means no user is logged in
        setUser(null);
      } else if (authResponse.data.isAuthenticated) {
        setUser(authResponse.data.user);
        if (authResponse.data.user.isOrganizer) {
          const organizedEvents = await api.getOrganizedEvents();
          setUser((prevUser) => ({
            ...prevUser,
            organizedEvents: organizedEvents.data,
          }));
        }
      } else {
        setUser(null);
        console.log("No user is logged in!");
      }
    } catch (error) {
      const status = error.response && error.response.status;
      // Only log errors that are not 401 or 204
      if (status !== 401 && status !== 204) {
        console.error("Auth check error:", error);
      }
      // Only a confirmed 401 means the session is actually invalid. Any other
      // failure (429 rate limit, 5xx, network blip) is inconclusive — clearing
      // the user here would wrongly log an already-authenticated admin out and
      // redirect them away mid-task (e.g. a rechecked-on-tab-focus request that
      // happens to get rate limited).
      if (status === 401) {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!isLoading && !user) {
      const isPublicRoute = publicRoutes.some((route) =>
        location.pathname.startsWith(route)
      );

      if (!isPublicRoute) {
        navigate("/");
      }
    }
  }, [user, isLoading, navigate, location.pathname]);

  // Checks for auth on tab change and window focus (for when user logs out on a different page and comes back to the home page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAuth();
      }
    };

    const handleFocus = () => {
      checkAuth();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkAuth]);

  useEffect(() => {
    if (isProduction && !isLoading) {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
        $pathname: location.pathname,
        $search: location.search,
      });
    }
  }, [location, posthog, isProduction, isLoading]);

  useEffect(() => {
    initGA4();
  }, []);

  // Bumped whenever the user makes a fresh consent decision (see
  // cookieConsent.js). trackPageView is gated on consent internally, so the
  // mount-time run below is a no-op for a first-time visitor who hasn't
  // decided yet — without this, accepting the banner without navigating
  // away would mean their landing page never gets a page_view. Routing this
  // through the same effect that already handles route changes keeps
  // trackPageView a single call site instead of also calling it from
  // CookieConsent directly.
  const [consentVersion, setConsentVersion] = useState(0);
  useEffect(() => {
    const handler = () => setConsentVersion((v) => v + 1);
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, handler);
  }, []);

  // GA4 page_view — fires on every route change, and also (via
  // consentVersion) immediately after a first-time consent decision, for
  // whichever page the user is currently on. Gated the same way as the
  // PostHog pageview above, so a route that redirects before auth resolves
  // doesn't get recorded as a page the user actually saw. A returning
  // visitor who already had consent never triggers the consentVersion path
  // (the banner isn't shown, so saveCookieConsent never runs again) — this
  // run is their one normal on-load page_view, not a duplicate.
  useEffect(() => {
    if (!isLoading) {
      trackPageView(`${location.pathname}${location.search}`);
    }
  }, [location, isLoading, consentVersion]);

  useEffect(() => {
    const initialLoadingElement = document.querySelector(".initial-loading");
    if (initialLoadingElement) {
      initialLoadingElement.remove();
    }
  }, []); // Run this effect only once on mount

  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false); // Set to true to enable maintenance mode

  if (isMaintenanceMode) {
    return (
      <div className="maintenance-page">
        <h1>Maintenance Mode</h1>
        <p>
          Apologies Our site is currently undergoing maintenance. Please check
          back later.
        </p>
      </div>
    );
  }

  if (isLoading && !isPublicPath) {
    return <AppLoadingSkeleton />;
  }

  return (
    <div className="App">
      <main className="main-content">
        <Outlet
          context={{
            user,
            setUser,
            handleLoginSuccess: (userData) => {
              setUser(userData);
              if (isProduction) {
                posthog.identify(userData.id, {
                  name: userData.name,
                  email: userData.email,
                });
              }
            },
            handleLogout: async () => {
              await api.logout();
              setUser(null);
              if (isProduction) {
                posthog.reset();
              }
              navigate("/");
            },
            language,
            setLanguage,
            isLoading,
          }}
        />
      </main>
      {location.pathname !== "/" && (
        // display: contents — this div only exists to bring the .landing-page
        // CSS-variable scope into play; it must not be a real box, or its
        // <footer> child (which needs to be .App's direct flex child, see
        // .sp-site-footer in app.css) would flex-grow the wrong element.
        <div className="landing-page" style={{ display: "contents" }}>
          <LandingFooter />
        </div>
      )}
      <CookieConsent />
    </div>
  );
}

export default AppContent;
